import { useState } from "react";
import { message, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { NewReviTherapyApi } from "../../api/revi"; // Ajusta ruta según ubicación
import { useAuth } from "../../hooks"; // Tu hook global de auth

const therapyApi = new NewReviTherapyApi();

export function useTherapyMutations({ onSuccess } = {}) {
  // Los avisos de este hook los lee el terapeuta, asi que son interfaz aunque
  // no haya JSX. Los botones de confirmar salen del bloque comun.
  const { t } = useTranslation(["misc", "common"]);
  const { accessToken } = useAuth();
  const [processing, setProcessing] = useState(false);

  // --- CREAR ---
  const create = async (payload) => {
    setProcessing(true);
    try {
      await therapyApi.createTherapy(accessToken, payload);
      message.success(t("therapies.created"));
      if (onSuccess) onSuccess();
      return true;
    } catch (error) {
      console.error(error);
      // error.message viene del servidor, ya traducido por Accept-Language.
      message.error(error.message || t("therapies.createError"));
      return false;
    } finally {
      setProcessing(false);
    }
  };

  // --- EDITAR ---
  const update = async (id, payload) => {
    setProcessing(true);
    try {
      await therapyApi.updateTherapy(accessToken, id, payload);
      message.success(t("therapies.saved"));
      if (onSuccess) onSuccess();
      return true;
    } catch (error) {
      message.error(t("therapies.updateError"));
      return false;
    } finally {
      setProcessing(false);
    }
  };

  // --- CLONAR ---
  const duplicate = async (therapy) => {
    setProcessing(true);
    try {
      await therapyApi.cloneTherapy(accessToken, therapy._id);
      // Convertirla en plantilla con hueco es reescribir la frase, no
      // sustituirla, y en frances el orden cambia. Reportada.
      message.success(t("therapies.cloned", { titulo: therapy.title }));
      if (onSuccess) onSuccess();
    } catch (error) {
      message.error(t("therapies.cloneError"));
    } finally {
      setProcessing(false);
    }
  };

  // --- BORRAR (Con Confirmación) ---
  const remove = (therapy) => {
    // 🕵️‍♂️ QA LOG 1: ¿Qué terapia estamos intentando borrar?
    console.group("🕵️‍♂️ QA AUDIT: DELETE ATTEMPT");
    console.log("1. Therapy Object Target:", therapy);
    console.log("   - ID a borrar:", therapy._id);
    console.log("   - Author (según frontend):", therapy.author);
    console.log("   - Institution:", therapy.institution);

    // 🕵️‍♂️ QA LOG 2: ¿Quién está firmando la petición?
    console.log("2. Access Token usado:", accessToken); // <-- IMPORTANTE

    // Decodificación rápida del token en consola (si no está encriptado) para ver el payload
    try {
      const payload = JSON.parse(atob(accessToken.split(".")[1]));
      console.log("3. Token Payload Decoded:", payload);
      console.log("   - User ID (sub/uid):", payload.user_id || payload.sub);
      console.log("   - Therapist ID (claim):", payload.therapist);
    } catch (e) {
      console.log("3. No se pudo decodificar el token manualmente:", e.message);
    }
    console.groupEnd();

    Modal.confirm({
      title: t("therapies.deleteTitle"),
      content: t("therapies.deleteBody", { titulo: therapy.title }),
      okText: t("common:actions.delete"),
      okType: "danger",
      cancelText: t("common:actions.cancel"),
      onOk: async () => {
        try {
          // ... tu llamada a la API ...
          await therapyApi.deleteTherapy(accessToken, therapy._id);
          message.success(t("therapies.deleted"));
          if (onSuccess) onSuccess();
        } catch (error) {
          console.error("❌ API ERROR RESPONSE:", error); // Ver el error crudo
          message.error(t("therapies.deleteError"));
        }
      },
    });
  };

  return {
    processing, // Estado de carga para spinners de botones
    create,
    update,
    duplicate,
    remove,
  };
}
